// src/components/admin/Category/Pricing/MasterPricingDefaultForm.jsx
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Loader2 } from 'lucide-react';

const defaultValues = {
    default_base_price_coins: 1,
};

export default function MasterPricingDefaultForm({ initialData, onSave, onCancel, isLoading }) {
    const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
        defaultValues: defaultValues,
    });

    useEffect(() => {
        if (initialData) {
            reset({
                default_base_price_coins: initialData.default_base_price_coins ?? 1,
            });
        } else {
            reset(defaultValues); // Reset to default if no initial data
        }
    }, [initialData, reset]);

    const onSubmit = (data) => {
        // Ensure the value is an integer before sending
        const payload = {
            default_base_price_coins: parseInt(data.default_base_price_coins, 10),
        };
        // Handle potential NaN if parsing fails, although validation should prevent it
        if (isNaN(payload.default_base_price_coins)) {
             payload.default_base_price_coins = 1; // Or null, depending on backend requirement
        }
        onSave(payload);
    };

    const errorTextClass = "text-sm text-destructive mt-1";

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
                <Label htmlFor="default_base_price_coins">Default Base Price (Coins) <span className="text-destructive">*</span></Label>
                <Controller
                    name="default_base_price_coins"
                    control={control}
                    rules={{
                        required: "Default base price is required.",
                        valueAsNumber: true,
                        min: { value: 1, message: "Default base price must be at least 1." },
                        validate: value => !isNaN(value) || "Must be a valid number."
                    }}
                    render={({ field }) => (
                        <Input
                            type="number"
                            id="default_base_price_coins"
                            {...field}
                            // Ensure empty string for null/undefined/NaN, otherwise string representation
                            value={field.value === null || typeof field.value === 'undefined' || isNaN(field.value) ? '' : String(field.value)}
                             onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} // Allow null if empty
                            className="mt-1"
                            placeholder="e.g., 100"
                        />
                    )}
                />
                {errors.default_base_price_coins && <p className={errorTextClass}>{errors.default_base_price_coins.message}</p>}
                <p className="text-sm text-muted-foreground mt-1">
                    This is the fallback base price used if a localized category doesn't have specific pricing set. Rules are not applied to this default price.
                </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>
                <Button type="submit" variant="default" disabled={isLoading || !isDirty}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {initialData ? 'Save Changes' : 'Save Default Price'}
                </Button>
            </div>
        </form>
    );
}